import {
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type { Dict, Init, LogOut } from '@sorare/wallet-shared';

import { useConfigContext } from '#src/contexts/config/index.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { messages } from '#src/contexts/wallet/messages.ts';
import { useFeatureFlags } from '#src/hooks/useFeatureFlags.tsx';
import { fiatCurrencySymbols } from '#src/lib/fiat.ts';
import { SIGNUP_AGE_LIMIT } from '#src/variables.ts';

const selectedFlags = ['lastTermsOfServiceUpdatedAt', 'usePasskeys'];

type SelectedFlagType = (typeof selectedFlags)[number];

const defaultsForSelectedFlags: { [key in SelectedFlagType]?: any } = {};

export const useInit = () => {
  const { sendRequest, registerHandler } = useContext(MessagingContext)!;
  const { currentUser, fiatCurrency } = useCurrentUserContext();
  const { formatMessage, dir } = useIntlContext();
  const { exchangeRate } = useConfigContext();

  const { flags } = useFeatureFlags();
  const [currentUserId, setCurrentUserId] = useState<string>();

  const dict = useMemo(
    () =>
      Object.entries(messages).reduce<Dict>((sum, cur) => {
        const key = cur[0] as keyof Dict;
        sum[key] = formatMessage(cur[1], {
          signupAgeLimit: SIGNUP_AGE_LIMIT,
        });
        return sum;
      }, {} as any),
    [formatMessage]
  );

  const featureFlags = useMemo(
    () => ({
      ...defaultsForSelectedFlags,
      ...Object.fromEntries(
        Object.entries(flags).filter(([key]) => selectedFlags.includes(key))
      ),
    }),
    [flags]
  );

  const onCurrentUserIdChange = useEffectEvent(
    (currentUserIdArg: Nullable<string>) => {
      if (currentUserId && currentUserIdArg !== currentUserId) {
        sendRequest<LogOut>('logOut', {
          flushMessagingQueue: true,
        });
        setCurrentUserId(undefined);
      }
    }
  );

  useEffect(() => {
    onCurrentUserIdChange(currentUser?.id);
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  useEffect(
    () =>
      registerHandler<Init>('init', async () => {
        const result = {
          dict,
          featureFlags,
          fiatCurrency,
          fiatCurrencySymbol: fiatCurrencySymbols[fiatCurrency],
          langDir: dir,
          ethRate: exchangeRate.getEthRate(fiatCurrency),
        };

        if (!currentUser) {
          setCurrentUserId(undefined);
          return { result };
        }

        const {
          id,
          email,
          nickname,
          wallet,
          starkKey,
          sorarePrivateKey,
          passkeys,
          hasPassword,
        } = currentUser;

        setCurrentUserId(id);

        return {
          result: {
            ...result,
            user: {
              email,
              nickname,
              address: wallet?.ethereumAddress || undefined,
              userPrivateKey: sorarePrivateKey || undefined,
              starkKey: starkKey || undefined,
              passkeys,
              hasPassword,
            },
          },
        };
      }),
    [
      exchangeRate,
      fiatCurrency,
      currentUser,
      dict,
      registerHandler,
      dir,
      featureFlags,
    ]
  );
};
